import moment from 'moment';

import { CONTRACT_TYPES } from '../../../../constants/reports/reports-data';
import Utils from '../../../../utils/immersive_utils';
import { handleFuturesPerAccount } from '../risk-data-per-account';

export const handleFutures = (riskType: string, breakdowns: any[], riskInstType: string[]) => {
  const hasAnyBooks = breakdowns.some((bk: any) => bk.book);

  if (!hasAnyBooks) {
    return handleFuturesPerAccount(riskType, breakdowns, riskInstType);
  }

  const calculateRiskSums = (items: any[]): number[] => {
    if (items.length === 0) return [];
    const maxLength = Math.max(...items.map((item) => item.risk.length));
    const sums = Array(maxLength).fill(0);
    items.forEach((item) => {
      item.risk.forEach((value: number, idx: number) => {
        sums[idx] += value;
      });
    });
    return sums;
  };

  const filteredBreakdowns = breakdowns.filter((bk) => {
    const isFuture =
      bk.contract === CONTRACT_TYPES.FUTURE && bk.instrument !== CONTRACT_TYPES.TOTAL;
    const isPerpetualTotal =
      bk.contract === CONTRACT_TYPES.PERPETUAL &&
      bk.instrument === CONTRACT_TYPES.TOTAL &&
      riskInstType.includes(CONTRACT_TYPES.PERPETUAL);
    return isFuture || isPerpetualTotal;
  });

  if (filteredBreakdowns.length === 0) {
    return null;
  }

  const globalAggregator = filteredBreakdowns.find(
    (bk) => bk.book === 'ALL' && bk.instrument === 'ALL' && bk.contract === CONTRACT_TYPES.FUTURE,
  );
  const globalValues = globalAggregator
    ? globalAggregator.risk
    : calculateRiskSums(filteredBreakdowns);

  const uniqueBooks = Array.from(
    new Set(filteredBreakdowns.filter((bk) => bk.book && bk.book !== 'ALL').map((bk) => bk.book)),
  );

  const bookChildren = uniqueBooks.map((bookName) => {
    const breakdownsForBook = filteredBreakdowns.filter((bk) => bk.book === bookName);

    const bookAggregator = breakdownsForBook.find((bk) => bk.instrument === 'ALL');
    const bookValues = bookAggregator ? bookAggregator.risk : calculateRiskSums(breakdownsForBook);

    const dateChildren = groupByFutureDate(riskType, breakdownsForBook, calculateRiskSums);

    return {
      id: `${riskType}-future-book-${bookName}`,
      label: bookName,
      position: 'Position',
      values: bookValues,
      children: dateChildren,
    };
  });

  return {
    id: `${riskType}-future`,
    label: 'Futures',
    position: 'Position',
    values: globalValues,
    children: bookChildren,
  };
};

function groupByFutureDate(
  riskType: string,
  breakdownsForBook: any[],
  calculateRiskSums: (items: any[]) => number[],
) {
  const dateMap = new Map<string, any[]>();

  breakdownsForBook.forEach((bk) => {
    if (bk.instrument === 'ALL') return;
    const parts = bk.instrument.split('-');
    if (parts.length < 2) return;
    const datePart = parts[1];
    if (!dateMap.has(datePart)) {
      dateMap.set(datePart, []);
    }
    dateMap.get(datePart)?.push(bk);
  });

  const dateNodes: any[] = [];
  dateMap.forEach((instruments, datePart) => {
    const aggregator = breakdownsForBook.find(
      (bk) => bk.instrument === 'ALL' && bk.contract === `${CONTRACT_TYPES.FUTURE}-${datePart}`,
    );

    const dateValues = aggregator ? aggregator.risk : calculateRiskSums(instruments);

    const instrumentNodes = instruments.map((instrumentBk) => ({
      id: `${riskType}-future-${instrumentBk.instrument}`,
      label: instrumentBk.instrument,
      values: instrumentBk.risk,
      position: instrumentBk.position,
    }));

    let dateLabel;
    try {
      dateLabel = moment(Utils.parseDate(datePart)).format('YYYY-MM-DD');
    } catch {
      dateLabel = datePart;
    }

    dateNodes.push({
      id: `${riskType}-future-date-${datePart}`,
      label: dateLabel,
      values: dateValues,
      position: 'Position',
      children: instrumentNodes,
    });
  });

  return dateNodes;
}
